import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import {
    ClickElement,
    GA4EventName,
    GA4FeatureCategory,
    trackAnalyticsEvent,
} from '@jsmdg/tracking';
import { Dropdown } from '@jsmdg/yoshi';
import { useRedemptionCart } from '../../hooks/useRedemptionCart';
import { RedeemInfo } from '../RedeemInfo';
import { RedemptionInfoBanner } from '../RedemptionInfoBanner/RedemptionInfoBanner';
import styles from './FriendsDiscountSelect.module.scss';

const trackDiscountSelectClick = (): void => {
    setTimeout(() => {
        trackAnalyticsEvent({
            eventData: {
                eventName: GA4EventName.ExpandDropdown,
                feature_category: GA4FeatureCategory.ProductdetailBuybox,
                click_element: ClickElement.OpenQuantityDropdown,
            },
        });
    }, 0);
};

const messages = defineMessages({
    singleVoucherSingular: {
        defaultMessage: '{numVouchers} Gutschein',
    },
    singleVoucherPlural: {
        defaultMessage: '{numVouchers} Gutscheine',
    },
    groupVoucherSingular: {
        defaultMessage: '{numVouchers}x ({participantsLabel} Personen)',
    },
    groupVoucherPlural: {
        defaultMessage: '{numVouchers}x (je {participantsLabel} Personen)',
    },
    redeemInfo: {
        defaultMessage: 'Flexibel einlösbar',
    },
});

type FriendsDiscountSelectProps = {
    readonly className?: string;
    readonly header?: string;
    readonly selectedSeatCount?: string;
    readonly handleSeatCountChange: (
        newSeatCount: string,
        trackSelection?: boolean,
    ) => Promise<void>;
    readonly minParticipants: number;
    readonly maxParticipants: number;
    readonly setDiscountSelectIsOpen: (isOpen: boolean) => void;
    readonly hasFriendsDiscount?: boolean;
    readonly showRedeemInfo?: boolean;
    readonly hasNoBookableLocations?: boolean;
};

const FriendsDiscountSelect = ({
    className,
    handleSeatCountChange,
    hasFriendsDiscount = false,
    hasNoBookableLocations = false,
    header,
    maxParticipants,
    minParticipants,
    selectedSeatCount,
    setDiscountSelectIsOpen,
    showRedeemInfo = false,
}: FriendsDiscountSelectProps): JSX.Element => {
    const intl = useIntl();

    const { isRedemptionCartFilled } = useRedemptionCart();

    const [useIosVersionFix, setUseIosVersionFix] = useState(false);

    const participantsLabel =
        minParticipants === maxParticipants
            ? minParticipants.toString()
            : `${minParticipants}-${maxParticipants}`;

    const getVoucherLabel = (numVouchers: number): string => {
        if (maxParticipants === 1) {
            if (numVouchers === 1) {
                return intl.formatMessage(messages.singleVoucherSingular, {
                    numVouchers,
                });
            }

            return intl.formatMessage(messages.singleVoucherPlural, {
                numVouchers,
            });
        }

        if (numVouchers === 1) {
            return intl.formatMessage(messages.groupVoucherSingular, {
                numVouchers,
                participantsLabel,
            });
        }

        return intl.formatMessage(messages.groupVoucherPlural, {
            numVouchers,
            participantsLabel,
        });
    };

    const options = Array.from(Array.from({ length: 25 }).keys()).map(key => ({
        label: getVoucherLabel(key + 1),
        value: (key + 1).toString(),
    }));

    useEffect(() => {
        if (navigator !== undefined) {
            setUseIosVersionFix(!!navigator?.userAgent?.includes('Version/15.'));
        }
    }, []);

    return (
        <div className={className}>
            {!!header && (
                <>
                    <div
                        className={classNames(
                            'font-complementary',
                            { 'mt-1-5x': hasFriendsDiscount },
                            styles.title,
                        )}
                    >
                        {header}
                    </div>
                    {showRedeemInfo && <RedeemInfo />}
                </>
            )}
            <Dropdown
                label={options[0].label}
                value={selectedSeatCount}
                ariaLabel={options[0].label}
                onChange={(newSeatCount: string) => {
                    handleSeatCountChange(newSeatCount, true);
                }}
                disabled={isRedemptionCartFilled}
                onClick={trackDiscountSelectClick}
                onClose={() => {
                    setDiscountSelectIsOpen(false);
                }}
                options={options}
                className={classNames({
                    'mt-1-5x': !!header,
                    'mt-1x': !header,
                })}
                dataTestId="select-num-tickets"
                overlayClasses={useIosVersionFix ? styles.ios15Fix : ''}
            />
            {isRedemptionCartFilled && hasNoBookableLocations && <RedemptionInfoBanner />}
        </div>
    );
};

export { FriendsDiscountSelect };
